import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import { useTimetableSync, SyncOverlay, SyncConfirmDialog, SyncReportDialog } from './TimetableSync';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface TimetableType {
  id: string; // Mongo ObjectID hex string
  name: string;
  session: string;
  status?: 'active' | 'inactive'; // populated by the backend (models.TimetableType.Status)
}

interface TimetableTypeFormState {
  name: string;
}

interface ManageTimetableTypesModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional — called after any successful add/set-active so the parent
   * (Timetable.tsx, wherever useTimetableTypes lives) can refetch its own
   * list/store. Wire this to whatever refresh function useTimetableTypes
   * exposes. */
  onChanged?: () => void;
}

const EMPTY_FORM: TimetableTypeFormState = { name: '' };

const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '9px 11px', borderRadius: '9px',
  border: '1.5px solid #e2e8f0', fontSize: '13.5px', color: '#1e293b',
  background: disabled ? '#f1f5f9' : 'white', outline: 'none', fontFamily: 'inherit',
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const ManageTimetableTypesModal: React.FC<ManageTimetableTypesModalProps> = ({
  open,
  onClose,
  onChanged,
}) => {
  const toast = useRef<Toast>(null);

  const [types, setTypes] = useState<TimetableType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Add-only inline panel — edit is intentionally not offered (see note
  // above handleSaveForm)
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<TimetableTypeFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Which type the user asked to sync (drives the confirmation dialog)
  const [confirmType, setConfirmType] = useState<TimetableType | null>(null);

  // Required by CreateTimetableType's binding:"required" on both session_id
  // and session. There is no free-text "session" field in this form anymore
  // — a hand-typed string like "2025-2026" doesn't correspond to a real
  // session document, so every create using it would 400. Source it from
  // the same store the other timetable/time-slot modals use instead.
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);
  const currentSessionName = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.session);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      // GET /timetable-types supports an optional session_id filter
      // (GetTimetableTypes). Without it this returns every timetable type
      // ever created for the school, across all sessions — scope it to the
      // current session, same as the time slots list.
      const response = await apiClient.get('/timetable-types', {
        params: currentSessionId ? { session_id: currentSessionId } : {},
      });
      if (response.data.status === 'success') {
        setTypes(response.data.data || []);
      } else {
        setTypes([]);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch timetable types';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [currentSessionId]);

  // Shared sync logic: API calls, full-screen overlay, problem report.
  const sync = useTimetableSync(toast, async () => {
    await fetchTypes();
    onChanged?.();
  });

  useEffect(() => {
    if (open) fetchTypes();
  }, [open, fetchTypes]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTypes([]);
      setLoading(true);
      setFormOpen(false);
    }, 400);
  };

  // ── Add ─────────────────────────────────────────────────────────────────
  const openAddForm = () => {
    setFormData(EMPTY_FORM);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormData(EMPTY_FORM);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Timetable type name is required.';
    if (formData.name.trim().length > 50) return 'Name cannot exceed 50 characters.';
    return null;
  };

  // NOTE: only CREATE is wired up. The backend's RegisterTimetableTypeRoutes
  // registers POST "", POST "/dynamic-insert", GET "", and
  // POST "/:id/set-active" only — there is no PUT or DELETE route for
  // timetable types. Editing/deleting from this modal isn't possible until
  // those are added server-side (update_timetable_type / delete_timetable_type
  // + routes), so those actions are disabled below rather than firing
  // requests that can only 404.
  const handleSaveForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: validationError, life: 4000 });
      return;
    }
    if (!currentSessionId) {
      toast.current?.show({
        severity: 'error',
        summary: 'No Active Session',
        detail: 'A current session is required to add a timetable type.',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        session_id: currentSessionId,
        session: currentSessionName,
        name: formData.name.trim(),
      };

      const response = await apiClient.post('/timetable-types', payload);

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Timetable type added.',
          life: 3500,
        });
        closeForm();
        await fetchTypes();
        onChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to add timetable type',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to add timetable type',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: '24px',
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0, border: '3px solid white',
        }}>
          <i className="pi pi-calendar" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            Timetable Types
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {types.length} type{types.length === 1 ? '' : 's'} • Session: {currentSessionName || 'Current Session'}
          </div>
        </div>
      </div>

      {!formOpen && (
        <button
          onClick={openAddForm}
          disabled={!currentSessionId}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
            padding: '9px 16px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white', fontSize: '13px', fontWeight: 700,
            cursor: currentSessionId ? 'pointer' : 'not-allowed',
            opacity: currentSessionId ? 1 : 0.6,
            boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: '12px' }} />
          Add
        </button>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '620px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: mttMaskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: mttMaskFadeOut 0.4s ease !important; }
          @keyframes mttMaskFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes mttMaskFadeOut { from { opacity: 1; } to { opacity: 0; } }
          .p-dialog { animation: mttDialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: mttDialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes mttDialogOpen { from { opacity: 0; transform: scale(0.92) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes mttDialogClose { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        {!currentSessionId ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '10px' }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '28px', color: '#f59e0b' }} />
            <span style={{ color: '#64748b', fontSize: '13.5px', textAlign: 'center' }}>
              No active session found. Please set a current session to manage timetable types.
            </span>
          </div>
        ) : (
          <>
            {/* ── Inline Add form ── */}
            {formOpen && (
              <div style={{
                background: 'white', borderRadius: '16px', padding: '18px',
                border: '1px solid #dbeafe', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                marginBottom: '14px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '14px' }}>
                  New Timetable Type
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    maxLength={50}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    disabled={saving}
                    placeholder="e.g., Regular, Exam, Ramadan"
                    style={inputStyle(saving)}
                  />
                  <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '5px' }}>
                    Will be added to the current session ({currentSessionName || 'Current Session'}).
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={closeForm}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                      background: 'white', color: '#475569', fontSize: '13px', fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveForm}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                      color: 'white', fontSize: '13px', fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                    }}
                  >
                    {saving ? 'Saving…' : 'Add'}
                  </button>
                </div>
              </div>
            )}

            {/* ── List ── */}
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading timetable types...</span>
              </div>
            ) : types.length === 0 && !formOpen ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '10px' }}>
                <i className="pi pi-inbox" style={{ fontSize: '32px', color: '#cbd5e1' }} />
                <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>No timetable types found</span>
              </div>
            ) : types.length > 0 ? (
              <div style={{
                background: 'white', borderRadius: '16px', padding: '8px',
                border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                {types.map((type, idx) => {
                  const isActive = type.status === 'active';
                  const isActivating = sync.syncingId === type.id;
                  const isChecking = sync.checkingId === type.id;

                  return (
                    <div
                      key={type.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        padding: '12px 10px',
                        borderBottom: idx === types.length - 1 ? 'none' : '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                        background: isActive ? '#f0fdf4' : '#f8fafc',
                        color: isActive ? '#166534' : '#94a3b8',
                        border: `1.5px solid ${isActive ? '#86efac' : '#e5e7eb'}`,
                      }}>
                        <i className="pi pi-calendar" style={{ fontSize: '14px' }} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937', marginBottom: '2px' }}>
                          {type.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                          Session: {type.session}
                        </div>
                      </div>

                      <span style={{
                        padding: '4px 12px', borderRadius: '7px',
                        border: `2px solid ${isActive ? '#86efac' : '#e5e7eb'}`,
                        background: isActive ? '#f0fdf4' : '#f8fafc',
                        color: isActive ? '#166534' : '#94a3b8',
                        fontSize: '11px', fontWeight: 700,
                        textTransform: 'capitalize', flexShrink: 0,
                      }}>
                        {type.status || 'unknown'}
                      </span>

                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={() => sync.check(type.id, type.name)}
                          disabled={sync.syncing || isChecking}
                          title="Check this timetable for problems"
                          style={{
                            width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(59,130,246,0.3)',
                            background: 'rgba(59,130,246,0.08)', color: '#2563eb',
                            cursor: sync.syncing || isChecking ? 'not-allowed' : 'pointer',
                            opacity: sync.syncing || isChecking ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <i className={isChecking ? 'pi pi-spin pi-spinner' : 'pi pi-search'} style={{ fontSize: '11px' }} />
                        </button>
                        <button
                          onClick={() => setConfirmType(type)}
                          disabled={sync.syncing}
                          title={isActive ? 'Sync teacher assignments' : 'Set active and sync'}
                          style={{
                            width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(16,185,129,0.3)',
                            background: 'rgba(16,185,129,0.08)', color: '#059669',
                            cursor: sync.syncing ? 'not-allowed' : 'pointer', opacity: sync.syncing && !isActivating ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <i
                            className={isActivating ? 'pi pi-spin pi-spinner' : isActive ? 'pi pi-sync' : 'pi pi-check'}
                            style={{ fontSize: '11px' }}
                          />
                        </button>
                        {/* Edit/Delete intentionally omitted: no PUT/DELETE
                            route exists yet for /timetable-types on the
                            backend (RegisterTimetableTypeRoutes only wires
                            up POST "", POST "/dynamic-insert", GET "", GET
                            "/:id/validate" and POST "/:id/set-active"). Re-add these once those
                            handlers + routes are added server-side. */}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </Dialog>

      <SyncOverlay visible={sync.syncing} />
      <SyncConfirmDialog
        typeName={confirmType?.name ?? null}
        isActive={confirmType ? confirmType.status === 'active' : undefined}
        sessionName={currentSessionName}
        onCancel={() => setConfirmType(null)}
        onConfirm={() => {
          const t = confirmType;
          setConfirmType(null);
          if (t) sync.sync(t.id, t.name, t.status === 'active');
        }}
      />
      <SyncReportDialog report={sync.report} onClose={sync.closeReport} />
    </>
  );
};

export default ManageTimetableTypesModal;